import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectFilteredUncancelledVirtualCards,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/Search/redux/search.selectors'
import AnimatedTableChevron from 'src/components/AnimatedTableChevron/AnimatedTableChevron'
import Box from 'src/components/Box/Box'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import KeywordFilter from 'src/components/KeywordFilter/KeywordFilter'
import LeftFlyout, {
  LeftFlyoutBodyWidths,
} from 'src/components/LeftFlyout/LeftFlyout'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import LoadingBoxBottom from 'src/components/LoadingBoxBottom/LoadingBoxBottom'
import SvgCard from 'src/components/Svgs/SvgCard'
import SvgDigits from 'src/components/Svgs/SvgDigits'
import SvgEmail from 'src/components/Svgs/SvgEmail'
import SvgExpenseCategory from 'src/components/Svgs/SvgExpenseCategory'
import SvgFunds from 'src/components/Svgs/SvgFunds'
import SvgProfile from 'src/components/Svgs/SvgProfile'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import {
  VirtualCardsFilters,
  VirtualCardsFiltersBody,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { AvatarSizes } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import {
  useDebounced,
  useElementScrollBottomHandler,
  useFlyoutDispatch,
} from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import Lnk from 'src/navigation/Lnk'
import { searchUrl, virtualCardDetailUrl } from 'src/navigation/urls'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardsCanIssueVirtualCardsCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface ExampleSearch {
  icon: React.ReactElement
  title: React.ReactNode
  subtitle: React.ReactNode
}

const Search: React.FC = () => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const users = useSelector(selectUserListModelsFromState)
  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const filteredUncancelledVirtualCards = useSelector(
    selectFilteredUncancelledVirtualCards,
  )
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsCanIssueVirtualCardsCountStatisticsFromState,
  )
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const appName = useSelector(selectAppName)

  const history = useHistory()
  /// eslint-disable-next-line @typescript-eslint/no-explicit-any
  const location = useLocation() as any // need this here because react-router interfaces are not up to date

  const dispatch = useFlyoutDispatch()

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsCanIssueVirtualCardsCountStatistics,
    ])
  }, [dispatchStatistics])

  useEffect((): void => {
    // set search query if there is something in the location already
    if (location.query.query) {
      setKeywordFilter(location.query.query)
    }
  }, [location.query.query])

  const fetchVirtualCards = (
    filtersBody: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getVirtualCards({
        statuses: [
          VirtualCardStatus.Active,
          VirtualCardStatus.Closed,
          VirtualCardStatus.Consumed,
          VirtualCardStatus.Expired,
          VirtualCardStatus.Pending,
        ],
        count: 10,
        page: page,
        ...filtersBody,
      }),
    )
  }

  const initialFilterValues: VirtualCardsFilters = useMemo(
    () => ({
      keyword: '',
      recipients: [],
    }),
    [],
  )

  const [keywordFilter, setKeywordFilter] = useState(
    initialFilterValues.keyword,
  )

  const filtersBody: VirtualCardsFiltersBody = useMemo(
    () => ({
      search: keywordFilter ? `${keywordFilter}` : undefined,
    }),
    [keywordFilter],
  )

  const { debouncedF } = useDebounced((): void => {
    if (virtualCardsFullPaginatedData.page !== undefined) {
      fetchVirtualCards(filtersBody)
    }
  })

  useEffect((): void => {
    debouncedF()
  }, [keywordFilter])

  useEffect((): void => {
    if (keywordFilter !== '') {
      analytics?.track('search flyout - search virtual cards - typing', {
        screen: '/search',
        intent: 'search virtual cards',
        interaction: 'typing',
        search: keywordFilter,
        results: virtualCardsFullPaginatedData.totalItems || 0,
      })
    }
  }, [virtualCardsFullPaginatedData.totalItems || keywordFilter])

  const handleScroll = useCallback((): void => {
    if (
      keywordFilter &&
      !virtualCardsFullPaginatedData.loading &&
      !virtualCardsFullPaginatedData.complete
    ) {
      fetchVirtualCards(
        filtersBody,
        virtualCardsFullPaginatedData.page !== undefined
          ? virtualCardsFullPaginatedData.page + 1
          : 0,
      )
    }
  }, [
    filtersBody,
    virtualCardsFullPaginatedData.loading,
    virtualCardsFullPaginatedData.complete,
    virtualCardsFullPaginatedData.page,
  ])
  const { onScroll, childRef, parentRef } =
    useElementScrollBottomHandler(handleScroll)

  const exampleSearches: ExampleSearch[] = [
    {
      icon: <SvgProfile />,
      title: translate('Card User Name'),
      subtitle: translate('e.g. Tina Turner'),
    },
    {
      icon: <SvgFunds />,
      title: translate('Available Credit'),
      subtitle: translate('e.g. >=500.00'),
    },

    {
      icon: <SvgCard />,
      title: translate('Card Name'),
      subtitle: translate('e.g. Monthly Subscription'),
    },

    {
      icon: <SvgEmail />,
      title: translate('Email'),
      subtitle: translate('e.g. tina@yourbusiness.com or yourbusiness.com'),
    },

    {
      icon: <SvgDigits />,
      title: translate('Last Four Digits'),
      subtitle: translate('e.g. 9245'),
    },

    {
      icon: <SvgExpenseCategory />,
      title: translate('Expense Categories'),
      subtitle: translate('e.g. Marketing'),
    },
  ]

  return (
    <LeftFlyout
      noPadding
      width={LeftFlyoutBodyWidths.Search}
      onScroll={onScroll}
      childRef={childRef}
      parentRef={parentRef}
    >
      <Helmet>
        <title>{`Search | ${appName}`}</title>
        <meta name="description" content={`Search page of ${appName}`} />
      </Helmet>
      {!statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded ? (
        <LoadingBackgroundPage />
      ) : (
        <>
          <span
            css={css`
              margin-top: 30px;
              ${getFontCss(
                32,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              padding: 40px 60px 0px 60px;

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                padding: 20px 20px 0px 20px;
              `)}
            `}
          >
            {translate('Search All Virtual Cards')}
          </span>
          <span
            css={css`
              margin-top: 40px;
              padding: 0px 60px;
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                padding: 0px 20px;
              `)}
            `}
          >
            <KeywordFilter
              forceFocus
              value={keywordFilter}
              onChange={setKeywordFilter}
              placeholder={translateSingleString(locale, 'Start Typing')}
            />
          </span>
          {!keywordFilter && virtualCardsFullPaginatedData.complete && (
            <TableEmptyState
              css={css`
                padding: 0px 60px;
                margin-top: 40px;
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  padding: 0px 20px;
                `)}
              `}
              design={TableEmptyStateDesigns.SearchVirtualCardsUnfiltered}
            />
          )}
          {!keywordFilter && !virtualCardsFullPaginatedData.complete && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                margin-top: 40px;
                padding: 0px 40px 40px 60px;

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  padding: 0px 20px 20px 20px;
                `)}
              `}
            >
              <FieldLabel>{translate('Example Searches')}</FieldLabel>
              <Box
                css={css`
                  display: flex;
                  flex-direction: column;
                  padding: 20px 0px;
                `}
              >
                {exampleSearches.map((exampleSearch, i) => (
                  <div
                    key={i}
                    css={css`
                      padding: 20px 40px;
                      display: flex;
                    `}
                  >
                    {exampleSearch.icon}
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        margin-left: 20px;
                      `}
                    >
                      <span
                        css={css`
                          ${getFontCss(
                            16,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        {exampleSearch.title}
                      </span>
                      <span
                        css={css`
                          margin-top: 4px;
                          ${getFontCss(
                            14,
                            styleConstants.colors.gray_dark,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        {exampleSearch.subtitle}
                      </span>
                    </div>
                  </div>
                ))}
              </Box>
            </div>
          )}
          {keywordFilter && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                margin-top: 40px;
                padding-bottom: 40px;

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  padding-bottom: 20px;
                `)}
              `}
            >
              {filteredUncancelledVirtualCards.length > 0 && (
                <Fragment>
                  <span
                    css={css`
                      ${getFontCss(
                        20,
                        themeConstants.colors.highlight,
                        styleConstants.fontFamilies.medium,
                      )};
                      padding: 0px 60px;

                      ${getBreakpointDown(breakpointSizes.XSM)(css`
                        padding: 0px 20px;
                      `)}
                    `}
                  >
                    {translate('${count} Results', {
                      count: `${virtualCardsFullPaginatedData.totalItems}`,
                    })}
                  </span>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      margin-top: 40px;
                    `}
                  >
                    {filteredUncancelledVirtualCards.map((vc, i) => (
                      <Lnk
                        css={css`
                          border-top: 1px solid
                            ${styleConstants.colors.gray_light};
                          padding: 30px 60px;
                          display: flex;
                          justify-content: space-between;
                          cursor: pointer;

                          &:hover {
                            background-color: ${styleConstants.colors
                              .blue_opacity};

                            .animated-table-chevron {
                              transform: translateX(4px) scale(calc(18 / 24));
                            }
                          }

                          ${getBreakpointDown(breakpointSizes.XSM)(css`
                            padding: 30px 20px;
                          `)}
                        `}
                        key={i}
                        to={virtualCardDetailUrl(vc.id)}
                        ariaLabelSource={'Virtual Card Details'}
                        onClick={(): void => {
                          // replace current location with the query term
                          history.replace({
                            pathname: searchUrl(),
                            search: `?query=${keywordFilter}`,
                            state: {
                              backgroundLocation: calculatedBackgroundLocation,
                              keywordFilter: keywordFilter,
                            },
                          })
                          analytics?.track(
                            'search flyout - selected virtual card from search - list item press',
                            {
                              screen: '/search',
                              intent: 'search virtual cards',
                              interaction: 'list item press',
                              virtualCardSelected: vc.displayName,
                              virtualCardId: vc.id,
                            },
                          )
                        }}
                      >
                        <div
                          css={css`
                            display: flex;
                            overflow: hidden;
                          `}
                        >
                          <UserAvatar
                            size={AvatarSizes.Medium}
                            user={users[vc.recipientId]}
                          />
                          <div
                            css={css`
                              display: flex;
                              flex-direction: column;
                              margin-left: 20px;
                              overflow: hidden;
                            `}
                          >
                            <div>
                              <span
                                css={css`
                                  ${getFontCss(
                                    14,
                                    themeConstants.colors.font_primary,
                                    styleConstants.fontFamilies.medium,
                                  )};
                                  ${(vc.isInactive || vc.isPending) &&
                                  getFontCss(
                                    14,
                                    styleConstants.colors.gray_dark,
                                    styleConstants.fontFamilies.medium,
                                  )};
                                `}
                              >
                                {vc.displayName}
                              </span>
                              {!vc.isPending && (
                                <span
                                  css={css`
                                    margin-left: 6px;
                                    ${getFontCss(
                                      12,
                                      styleConstants.colors.gray_darkest,
                                      styleConstants.fontFamilies.regular,
                                    )};
                                  `}
                                >
                                  {`(${vc.last4})`}
                                </span>
                              )}
                            </div>
                            {creditCardsCanIssueVirtualCardsCount === 0 && (
                              <span
                                css={css`
                                  margin-top: 4px;
                                  ${getFontCss(
                                    12,
                                    styleConstants.colors.gray_darkest,
                                    styleConstants.fontFamilies.regular,
                                  )};
                                `}
                              >
                                {!vc.isInactive &&
                                  translate('Active Until: ${validTo}', {
                                    validTo: (
                                      <span
                                        css={css`
                                          ${getFontCss(
                                            12,
                                            themeConstants.colors.font_primary,
                                            styleConstants.fontFamilies.regular,
                                          )};
                                        `}
                                      >
                                        {vc.activeUntilTimezonedString}
                                      </span>
                                    ),
                                  })}
                                {vc.isInactive &&
                                  vc.inactiveSinceTimezonedString !==
                                    undefined &&
                                  translate('Inactive since ${date}', {
                                    date: vc.inactiveSinceTimezonedString,
                                  })}
                              </span>
                            )}
                            {creditCardsCanIssueVirtualCardsCount > 0 && (
                              <span
                                css={css`
                                  white-space: nowrap;
                                  overflow: hidden;
                                  text-overflow: ellipsis;
                                  ${getFontCss(
                                    12,
                                    styleConstants.colors.gray_darkest,
                                    styleConstants.fontFamilies.regular,
                                  )};
                                `}
                              >
                                {translate('Card User: ${recipientName}', {
                                  recipientName: (
                                    <span
                                      css={css`
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                        ${getFontCss(
                                          12,
                                          styleConstants.colors.gray_darkest,
                                          styleConstants.fontFamilies.regular,
                                        )};
                                      `}
                                    >
                                      {users[vc.recipientId].fullNameOrEmail}
                                    </span>
                                  ),
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          css={css`
                            display: flex;
                            align-items: flex-start;
                          `}
                        >
                          <span
                            css={css`
                              ${getFontCss(
                                14,
                                themeConstants.colors.highlight,
                                styleConstants.fontFamilies.regular,
                              )};
                              ${vc.isPending &&
                              getFontCss(
                                14,
                                styleConstants.colors.gray_dark,
                                styleConstants.fontFamilies.regular,
                              )};
                              ${vc.isInactive &&
                              getFontCss(
                                14,
                                styleConstants.colors.red,
                                styleConstants.fontFamilies.regular,
                              )};
                            `}
                          >
                            {vc.isActive
                              ? vc.balanceLabel
                              : vc.psuedoStatusLabel}
                          </span>
                          <AnimatedTableChevron
                            css={css`
                              transform-origin: top;
                              transform: scale(calc(18 / 24));
                            `}
                          />
                        </div>
                      </Lnk>
                    ))}
                  </div>
                </Fragment>
              )}
              {filteredUncancelledVirtualCards.length === 0 &&
                virtualCardsFullPaginatedData.complete && (
                  <TableEmptyState
                    design={TableEmptyStateDesigns.SearchVirtualCardsFiltered}
                    title={translate(
                      'Hmm, nothing seems to match "${keywordFilter}"',
                      {
                        keywordFilter: keywordFilter,
                      },
                    )}
                  />
                )}
              {virtualCardsFullPaginatedData.loading && <LoadingBoxBottom />}
            </div>
          )}
        </>
      )}
    </LeftFlyout>
  )
}

export default Search
